import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { updateDb } from "../services/updateDb";

import LoadPage from "../common/LoadPage";
import ConfirmationModal from "../common/ConfirmationModal";
import AlertMessage from "../common/AlertMessage";

import useAxiosFetch from "../common/useAxiosFetch";
import InfoTooltip from "../common/InfoTooltip";
import { Button } from "react-bootstrap";
import { useAppContext } from "../AppContext";
import { useTranslation } from "react-i18next";
import { MODAL_TYPES } from "../modals/ModalManager";

const UserPage = () => {
  const { t } = useTranslation();
  const { currentUser, showAlertMessage, alertMessage, openModal } = useAppContext();
  const user = JSON.parse(localStorage.getItem("user"));

  const navigate = useNavigate();
  const { data, loading, refetch } = useAxiosFetch("/api/users"); 
  const { data: featureLimits } = useAxiosFetch("/api/features/limits");

  const [show, setShow] = useState(false);
  const [deleteUser, setDeleteUser] = useState();
  const [resendingUserId, setResendingUserId] = useState(null);

  const handleClose = async (deleteUser, userId) => {
    setShow(false);

    if (deleteUser && userId) {
      const res = await updateDb("/api/users/" + userId, {}, "delete");

      if (res.success) {
        showAlertMessage('error', t('user') + ' ' + t('deleted'));
        refetch();
      }
      else {
        showAlertMessage('error', res.error ? res.error : 'Error deleting user.');
      }
    }
  };

  const handleShow = (userId) => {
    setDeleteUser(userId);
    setShow(true);
  };

  let navigateEditUser = (userId) => {
    navigate(`/edituser?userId=${userId}`);
  };

  const handleResendActivation = async (userId) => {
    setResendingUserId(userId);
    const res = await updateDb("/api/users/resend-activation/" + userId, {});

    if (res.success) {
      showAlertMessage('success', t('activation_email_resent'));
    } else {
      showAlertMessage('error', res.error ? res.error : t('activation_email_resend_error'));
    }
    setResendingUserId(null);
  };

  const handleAddUser = (e) => {
    e.preventDefault();
    
    if (featureLimits) {
      const { current } = featureLimits.usage.users;
      const maxUsers = featureLimits.limits.maxUsers;
      
      // Check if limit reached
      if (maxUsers !== -1 && current >= maxUsers) {
        openModal(MODAL_TYPES.UPGRADE, {
          featureType: 'Users',
          currentLimit: maxUsers,
          currentCount: current,
          recommendedPlan: 'starter'
        });
        return;
      }
    }
    
    navigate('/adduser');
  };

  if (loading) 
    return <LoadPage />;

  return (
    <div id="content">
      <div id="content-title">{t('users')}</div>

      <div className="clear-both" />

      <ConfirmationModal
        show={show}
        handleClose={handleClose}
        deleteId={deleteUser}
      />

      <Button 
        className="add-btn btn-right-top" 
        variant="primary" 
        type="button"
        onClick={handleAddUser}
      >
        +
      </Button>

      <AlertMessage type={alertMessage.type} message={alertMessage.content} />

      <div className="content-bubble content-bubble-table">
        <table className="overview-table">
          <thead>
            <tr>
              <td>{t('name')}</td>
              <td>Email</td>
              <td>{t('role')}</td>
              <td>{t('active')}</td>
              <td>SSO</td>
              <td>{t('organization')}</td>
              <td>{t('domain')}</td>
              <td />
            </tr>
          </thead>
          <tbody>
            {data.map((item) => {
              let showEdit = true;

              if (!user || (user.role !== "superadmin" && item.role === "superadmin"))
                showEdit = false;

              return (
                <tr key={item["id"]}>
                  <td>{item.name}</td>
                  <td>{item.email}</td>
                  <td>{item.role}</td>
                  <td>
                    {item.isActive ? "✔" : (
                      <InfoTooltip content={<><b>{t('resend_activation_email')}</b><br/>{t('resend_activation_tooltip')}</>}>
                        <i
                          onClick={() => resendingUserId !== item.id && handleResendActivation(item.id)}
                          className="icon-mail"
                          style={{ cursor: resendingUserId === item.id ? "wait" : "pointer", opacity: resendingUserId === item.id ? 0.5 : 1 }}
                        />
                      </InfoTooltip>
                    )}
                  </td>
                  <td>{item.ssoEnabled ? "✔" : ""}</td>
                  <td>{item.orgName}</td>
                  <td>{item.projectName}</td>
                  <td>
                    <div style={{ display: showEdit ? "" : "none" }}>
                      <i
                        onClick={() => navigateEditUser(item["id"])}
                        className="icon-edit"
                      />

                    {item.id !== currentUser.user_id  ? (
                      <i
                        onClick={() => handleShow(item["id"])}
                        className="icon-delete"
                      />) : (
                      <i className="icon-delete" style={{color: "gray", cursor: "default"}} />
                    ) }

                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserPage;
